// src/views/bots/flow/components/BotFlowTour.tsx
// Guided tour for the Visual Bot Flow Builder — auto-shows on first visit

import React, { useEffect, useState } from 'react';
import { Steps } from 'intro.js-react';
import 'intro.js/minified/introjs.min.css';

const TOUR_STORAGE_KEY = 'botFlowTourComplete';

interface BotFlowTourProps {
  isEdit?: boolean;
}

const BotFlowTour: React.FC<BotFlowTourProps> = ({ isEdit = false }) => {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(TOUR_STORAGE_KEY) !== 'true';
    } catch {
      return true;
    }
  });

  const steps = [
    {
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 12px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Welcome to the WhatsApp Bot Builder
          </h3>
          <p style="color: #475569; margin-bottom: 12px;">
            Create bots that <strong>automatically reply to incoming WhatsApp messages</strong> — like pricing inquiries, product catalogues, appointment booking, and more.
          </p>
          <ul style="list-style: none; padding: 0; margin: 0 0 16px 0;">
            <li style="margin-bottom: 8px; display: flex; align-items: center; color: #1e293b;">
              <span style="margin-right: 8px; color: #10b981;">1.</span>
              Set trigger keywords (e.g. "price", "menu", "hi")
            </li>
            <li style="margin-bottom: 8px; display: flex; align-items: center; color: #1e293b;">
              <span style="margin-right: 8px; color: #3b82f6;">2.</span>
              Build the conversation flow visually
            </li>
            <li style="margin-bottom: 8px; display: flex; align-items: center; color: #1e293b;">
              <span style="margin-right: 8px; color: #8b5cf6;">3.</span>
              Collect lead info like name, email, phone
            </li>
            <li style="margin-bottom: 8px; display: flex; align-items: center; color: #1e293b;">
              <span style="margin-right: 8px; color: #f59e0b;">4.</span>
              Preview and test before going live
            </li>
          </ul>
          <p style="margin: 0; color: #64748b; font-style: italic;">Quick walkthrough — takes about 1 minute</p>
        </div>
      `,
    },
    {
      element: '[data-tour="bot-header"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Bot Settings
          </h3>
          <p style="color: #475569; margin-bottom: 8px;">
            Give your bot a <strong>clear name</strong> (e.g. "Price Inquiry Bot", "Catalogue Bot"). Then configure:
          </p>
          <ul style="list-style: none; padding: 0; margin: 0;">
            <li style="margin-bottom: 6px; color: #1e293b;">
              <strong>Active/Inactive</strong> — Turn the bot on or off
            </li>
            <li style="margin-bottom: 6px; color: #1e293b;">
              <strong>All Leads / New Leads</strong> — Respond to all leads or only new ones
            </li>
            <li style="color: #1e293b;">
              <strong>Description</strong> — Optional notes for your team
            </li>
          </ul>
        </div>
      `,
      position: 'bottom',
    },
    {
      element: '[data-tour="start-node"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Start Node — Trigger Keywords
          </h3>
          <p style="color: #475569; margin-bottom: 8px;">
            <strong>Click this node</strong> and type the keywords that activate this bot.
          </p>
          <p style="color: #475569; margin-bottom: 8px;">
            When someone sends a WhatsApp message containing any of these keywords, the bot starts the conversation automatically.
          </p>
          <div style="background: #f0fdf4; padding: 8px 12px; border-radius: 6px; border-left: 3px solid #10b981;">
            <p style="margin: 0; color: #166534; font-size: 13px;">
              <strong>Examples:</strong> "price", "catalogue", "hello", "support"<br/>
              <strong>Tip:</strong> Type "any" to respond to every incoming message.
            </p>
          </div>
        </div>
      `,
      position: 'right',
    },
    {
      element: '[data-tour="node-palette"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Node Palette
          </h3>
          <p style="color: #475569;">
            This sidebar contains all the node types you can use. <strong>Drag any node</strong> from here onto the canvas to add it to your bot's flow. Let's look at each one.
          </p>
        </div>
      `,
      position: 'right',
    },
    {
      element: '[data-tour="node-interactive-button"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            🔘 Button Question
          </h3>
          <p style="color: #475569; margin-bottom: 8px;">
            Shows <strong>2-3 quick-reply buttons</strong> in WhatsApp. The user taps one to continue.
          </p>
          <div style="background: #f0f9ff; padding: 8px 12px; border-radius: 6px; border-left: 3px solid #3b82f6;">
            <p style="margin: 0; color: #1e40af; font-size: 13px;">
              <strong>Example:</strong> "What are you looking for?"<br/>
              Buttons: <em>Pricing</em> | <em>Catalogue</em> | <em>Support</em>
            </p>
          </div>
        </div>
      `,
      position: 'right',
    },
    {
      element: '[data-tour="node-interactive-list"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            📋 List Question
          </h3>
          <p style="color: #475569; margin-bottom: 8px;">
            Shows a <strong>dropdown menu</strong> with 4-10 options, organized into sections. Ideal for longer lists.
          </p>
          <div style="background: #faf5ff; padding: 8px 12px; border-radius: 6px; border-left: 3px solid #8b5cf6;">
            <p style="margin: 0; color: #6b21a8; font-size: 13px;">
              <strong>Example:</strong> "Select a product category"<br/>
              Options: <em>Electronics, Clothing, Home, Sports...</em>
            </p>
          </div>
        </div>
      `,
      position: 'right',
    },
    {
      element: '[data-tour="node-text"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            ✍️ Text, Email, Phone & Number
          </h3>
          <p style="color: #475569; margin-bottom: 8px;">
            These nodes <strong>ask the user to type a response</strong>. Each type validates the input:
          </p>
          <ul style="list-style: none; padding: 0; margin: 0;">
            <li style="margin-bottom: 4px; color: #1e293b; font-size: 13px;">
              <strong>Text</strong> — Any free-form response (name, feedback)
            </li>
            <li style="margin-bottom: 4px; color: #1e293b; font-size: 13px;">
              <strong>Email</strong> — Validates email format
            </li>
            <li style="margin-bottom: 4px; color: #1e293b; font-size: 13px;">
              <strong>Phone</strong> — Validates phone number
            </li>
            <li style="color: #1e293b; font-size: 13px;">
              <strong>Number</strong> — Accepts only numbers (budget, quantity)
            </li>
          </ul>
        </div>
      `,
      position: 'right',
    },
    {
      element: '[data-tour="node-location"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            📍 Location
          </h3>
          <p style="color: #475569;">
            Asks the user to <strong>share their location</strong> via WhatsApp's built-in location sharing. Useful for delivery, store locator, or service area bots.
          </p>
        </div>
      `,
      position: 'right',
    },
    {
      element: '[data-tour="flow-canvas"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Canvas — Your Workspace
          </h3>
          <p style="color: #475569; margin-bottom: 8px;">
            This is where you design the conversation:
          </p>
          <ul style="list-style: none; padding: 0; margin: 0;">
            <li style="margin-bottom: 6px; color: #1e293b;">
              <span style="color: #3b82f6; margin-right: 6px;">&#8226;</span>
              <strong>Connect nodes</strong> — Drag from a circle (handle) on one node to another
            </li>
            <li style="margin-bottom: 6px; color: #1e293b;">
              <span style="color: #3b82f6; margin-right: 6px;">&#8226;</span>
              <strong>Edit text</strong> — Click any node to select it, then edit the message inline
            </li>
            <li style="margin-bottom: 6px; color: #1e293b;">
              <span style="color: #3b82f6; margin-right: 6px;">&#8226;</span>
              <strong>Delete</strong> — Select a node or connection, then press Delete key
            </li>
            <li style="color: #1e293b;">
              <span style="color: #3b82f6; margin-right: 6px;">&#8226;</span>
              <strong>Navigate</strong> — Scroll to zoom, drag empty space to pan around
            </li>
          </ul>
        </div>
      `,
      position: 'left',
    },
    {
      element: '[data-tour="auto-align"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Auto Align
          </h3>
          <p style="color: #475569;">
            Click this to <strong>neatly arrange</strong> all your nodes in a clean layout.
            Useful when things get messy after adding several nodes.
          </p>
        </div>
      `,
      position: 'bottom',
    },
    {
      element: '[data-tour="preview-btn"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Preview Your Bot
          </h3>
          <p style="color: #475569; margin-bottom: 8px;">
            Test your bot in a <strong>live WhatsApp chat simulation</strong>.
            See exactly how your customers will experience the conversation — click buttons, pick options, and follow the flow.
          </p>
          <p style="color: #64748b; font-size: 13px; margin: 0;">
            Available once you add at least one question node to your flow.
          </p>
        </div>
      `,
      position: 'bottom',
    },
    {
      element: '[data-tour="save-btn"]',
      intro: `
        <div style="text-align: left;">
          <h3 style="margin-bottom: 8px; font-family: 'Gilroy', system-ui, sans-serif; color: #0f172a;">
            Save & Go Live
          </h3>
          <p style="color: #475569; margin-bottom: 8px;">
            Once your flow is ready, click <strong>Save</strong> to create the bot. If the bot is set to Active, it will start responding on WhatsApp immediately.
          </p>
          <div style="background: #eff6ff; padding: 8px 12px; border-radius: 6px; border-left: 3px solid #3b82f6;">
            <p style="margin: 0; color: #1e40af; font-size: 13px;">
              <strong>Tip:</strong> When editing an existing bot, your changes auto-save every 30 seconds — no work lost!
            </p>
          </div>
        </div>
      `,
      position: 'bottom',
    },
  ];

  const saveTourState = (type = 'normal'): boolean => {
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      localStorage.setItem('botFlowTourMetadata', JSON.stringify({
        completedAt: new Date().toISOString(),
        completionType: type,
        version: '1.0',
      }));
      return true;
    } catch {
      return false;
    }
  };

  const onExit = (): void => {
    if (saveTourState('normal')) {
      setEnabled(false);
    }
  };

  // Inject custom Gilroy styles for intro.js tooltips
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'bot-flow-tour-styles';
    style.textContent = `
      .introjs-tooltip {
        min-width: 400px !important;
        max-width: 500px !important;
        padding: 20px !important;
        border-radius: 12px !important;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18) !important;
        font-family: "Gilroy", system-ui, -apple-system, sans-serif !important;
      }
      .introjs-tooltiptext {
        font-size: 14px !important;
        line-height: 1.6 !important;
        color: #334155 !important;
        padding: 0 !important;
      }
      .introjs-button {
        font-family: "Gilroy", system-ui, sans-serif !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        border-radius: 8px !important;
        padding: 8px 16px !important;
        text-shadow: none !important;
        border: none !important;
        transition: all 0.15s ease !important;
      }
      .introjs-nextbutton, .introjs-donebutton {
        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
        color: #fff !important;
      }
      .introjs-nextbutton:hover, .introjs-donebutton:hover {
        background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
      }
      .introjs-prevbutton {
        background: #f1f5f9 !important;
        color: #475569 !important;
      }
      .introjs-prevbutton:hover {
        background: #e2e8f0 !important;
      }
      .introjs-skipbutton {
        color: #94a3b8 !important;
        font-size: 12px !important;
      }
      .introjs-helperLayer {
        border-radius: 12px !important;
        box-shadow: 0 0 0 5000px rgba(15, 23, 42, 0.6) !important;
      }
      .introjs-bullets ul li a.active {
        background: #3b82f6 !important;
      }
      .introjs-progressbar {
        background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%) !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existing = document.getElementById('bot-flow-tour-styles');
      if (existing) document.head.removeChild(existing);
    };
  }, []);

  if (!enabled) return null;

  return (
    <Steps
      enabled={enabled}
      steps={steps}
      initialStep={0}
      onExit={onExit}
      options={{
        dontShowAgain: true,
        dontShowAgainLabel: "Don't show again",
        nextLabel: 'Next →',
        prevLabel: '← Back',
        doneLabel: 'Got it!',
        showStepNumbers: false,
        showBullets: true,
        showProgress: true,
        exitOnOverlayClick: false,
        exitOnEsc: true,
        scrollToElement: true,
        overlayOpacity: 0.6,
      }}
    />
  );
};

/** Reset tour so it shows again on next visit */
export const resetBotFlowTour = (): void => {
  try {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    localStorage.removeItem('botFlowTourMetadata');
  } catch { /* ignore */ }
};

export default BotFlowTour;
